import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Zap, ArrowLeft, ArrowRight, CheckCircle2, Mail, Shield, Sparkles, Package,
} from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import StarterKitCompatibilityStep from '@/components/energy-control/StarterKitCompatibilityStep';
import StarterKitDetailsStep from '@/components/energy-control/StarterKitDetailsStep';
import StarterKitSummary from '@/components/energy-control/StarterKitSummary';
import StarterKitFirstStep from '@/components/energy-control/StarterKitFirstStep';
import StarterKitNextSteps from '@/components/energy-control/StarterKitNextSteps';
import PwaInstallHelp from '@/components/energy-control/PwaInstallHelp';
import Phase1CommerceSection from '@/components/fase1/Phase1CommerceSection';
import EnergyControlFeatureGrid from '@/components/energy-control/EnergyControlFeatureGrid';
import MetenStartBundle from '@/components/energy-control/MetenStartBundle';
import EmergencyPowerModule from '@/components/emergency-power/EmergencyPowerModule';
import PilotFeedbackForm, { PilotBadge } from '@/components/pilot/PilotFeedbackForm';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';

const API = '/api';

const RECAP_LABELS = {
  meter_type: {
    smart_p1: 'Slimme meter met P1-poort',
    smart_p1_unclear: 'Slimme meter, P1 onbekend',
    old_unclear: 'Oude meter / analoge meter',
    unknown: 'Weet ik niet',
  },
  phase_type: {
    one_phase: '1-fase aansluiting',
    three_phase: '3-fase aansluiting',
    unknown: 'Weet ik niet',
  },
  live_insight: {
    has_dashboard: 'Ik heb al live inzicht',
    no_dashboard: 'Nog geen live inzicht',
    unknown: 'Weet ik niet',
  },
  preference: {
    diy: 'Zelf installeren',
    professional: 'Installateur laten installeren',
    advice_first: 'Eerst advies',
  },
};

const INITIAL = {
  meter_type: '',
  phase_type: '',
  live_insight: '',
  name: '',
  email: '',
  phone: '',
  street: '',
  postcode: '',
  city: '',
  preference: '',
  message: '',
  // Preload: the user can click "Vraag hulp aan" on a specific product,
  // we capture which one so the admin can see the interest trigger.
  interested_product_id: '',
};

export default function EnergyControlStarterKitPage() {
  const { intakeId } = useParams();
  const navigate = useNavigate();

  // --- Product catalog + recommendations state ---
  const [recs, setRecs] = useState(null); // { copy, top, categories }
  const [recsLoading, setRecsLoading] = useState(true);

  // --- Lead form state (scrolled into view when "Vraag hulp aan" is clicked) ---
  const [leadOpen, setLeadOpen] = useState(false);
  const [leadStep, setLeadStep] = useState(1); // 1=compat, 2=details, 3=done
  const [data, setData] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const [emailReportStatus, setEmailReportStatus] = useState('idle'); // idle|sending|success|error
  const [emailReportError, setEmailReportError] = useState('');
  const [pilotInfo, setPilotInfo] = useState({ is_pilot: false, pilot_type: null });

  const leadFormRef = useRef(null);

  // Track page view
  useEffect(() => {
    if (window.pwTrack) window.pwTrack('energy_control_starter_kit_view', { intake_id: intakeId });
  }, [intakeId]);

  // Pilot detection — call GET /energy-control/intake/{id} to read is_pilot.
  useEffect(() => {
    if (!intakeId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/energy-control/intake/${intakeId}`);
        if (!cancelled) {
          setPilotInfo({
            is_pilot: !!res.data?.is_pilot,
            pilot_type: res.data?.pilot_type || null,
          });
        }
      } catch {
        if (!cancelled) setPilotInfo({ is_pilot: false, pilot_type: null });
      }
    })();
    return () => { cancelled = true; };
  }, [intakeId]);

  // Load personalized recommendations on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setRecsLoading(true);
      try {
        const params = intakeId ? `?intake_id=${encodeURIComponent(intakeId)}` : '';
        const res = await axios.get(`${API}/energy-control/products/recommendations${params}`);
        if (!cancelled) setRecs(res.data);
      } catch {
        if (!cancelled) setRecs({ copy: null, top: [], categories: [] });
      } finally {
        if (!cancelled) setRecsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [intakeId]);

  const onUpdate = (patch) => {
    setData((prev) => ({ ...prev, ...patch }));
    setErrors({});
  };

  const openLeadForm = (productId = '') => {
    setData((prev) => ({ ...prev, interested_product_id: productId }));
    setLeadOpen(true);
    setLeadStep(1);
    setTimeout(() => {
      if (leadFormRef.current) leadFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
    if (window.pwTrack) {
      window.pwTrack('energy_control_starter_kit_help_click', { intake_id: intakeId, product_id: productId });
    }
  };

  const validateLeadStep = () => {
    const e = {};
    if (leadStep === 1) {
      if (!data.meter_type) e.meter_type = 'Maak een keuze';
      if (!data.phase_type) e.phase_type = 'Maak een keuze';
      if (!data.live_insight) e.live_insight = 'Maak een keuze';
    } else if (leadStep === 2) {
      if (!data.name.trim()) e.name = 'Vul uw naam in';
      if (!data.email.trim()) e.email = 'Vul uw e-mail in';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) e.email = 'Ongeldig e-mailadres';
      if (!data.phone.trim()) e.phone = 'Vul uw telefoonnummer in';
      if (!data.street.trim()) e.street = 'Vul uw straat + huisnummer in';
      if (!data.postcode.trim()) e.postcode = 'Vul uw postcode in';
      if (!data.city.trim()) e.city = 'Vul uw stad in';
      if (!data.preference) e.preference = 'Maak een keuze';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleLeadNext = () => {
    if (!validateLeadStep()) { toast.error('Controleer de verplichte velden'); return; }
    setLeadStep(leadStep + 1);
  };

  const handleLeadBack = () => {
    if (leadStep > 1) { setLeadStep(leadStep - 1); setErrors({}); }
    else setLeadOpen(false);
  };

  const handleLeadSubmit = async () => {
    if (!validateLeadStep()) { toast.error('Controleer de verplichte velden'); return; }
    setLoading(true);
    try {
      const payload = {
        intake_id: intakeId || null,
        cta_type: 'start_package',
        name: data.name.trim(),
        email: data.email.trim(),
        phone: data.phone.trim() || null,
        postcode: data.postcode.trim() || null,
        message: [data.message.trim(), data.interested_product_id ? `Interesse in: ${data.interested_product_id}` : '']
          .filter(Boolean).join('\n') || null,
        compatibility: {
          meter_type: data.meter_type,
          phase_type: data.phase_type,
          live_insight: data.live_insight,
          interested_product_id: data.interested_product_id || null,
        },
        address: {
          street: data.street.trim(),
          postcode: data.postcode.trim(),
          city: data.city.trim(),
        },
        preference: data.preference,
      };
      await axios.post(`${API}/energy-control/cta-lead`, payload);
      if (window.pwTrack) window.pwTrack('energy_control_starter_kit_submit', { intake_id: intakeId, preference: data.preference });
      setConfirmed(true);
      setLeadStep(3);
      toast.success('Uw adviesaanvraag is ontvangen. U krijgt ook een bevestiging per e-mail.');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Er ging iets mis. Probeer het opnieuw.');
    } finally {
      setLoading(false);
    }
  };

  // --- Ontvang dit pakket per e-mail (reuses the existing report-email endpoint) ---
  const emailRx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const [pkgEmail, setPkgEmail] = useState('');
  const handleSendPackageEmail = async () => {
    const em = (pkgEmail || '').trim();
    if (!emailRx.test(em)) {
      setEmailReportError('Ongeldig e-mailadres');
      setEmailReportStatus('error');
      return;
    }
    if (!intakeId) {
      setEmailReportError('Geen intake-id gevonden — rond eerst de intake af.');
      setEmailReportStatus('error');
      return;
    }
    setEmailReportStatus('sending');
    setEmailReportError('');
    try {
      const res = await axios.post(`${API}/energy-control/email-report`, { intake_id: intakeId, email: em });
      if (res.data?.success) {
        setEmailReportStatus('success');
        toast.success('Plan naar u verzonden');
      } else {
        setEmailReportStatus('error');
        setEmailReportError(res.data?.message || 'Verzenden mislukt');
      }
    } catch (err) {
      setEmailReportStatus('error');
      setEmailReportError(err?.response?.data?.detail || err.message || 'Verzenden mislukt');
    }
  };

  const copy = recs?.copy || null;
  // Fase 1G — when the new EC v2 €19,95 commerce section is live, hide the
  // legacy `powersure_monitoring` product card to avoid price confusion
  // (its card historically showed €19,95 while its CTA hit the legacy €9,95
  // endpoint). The backend endpoint stays available for grandfathered
  // subscribers; this filter only affects the public new-customer flow.
  const { flags: _ecFlags } = useFeatureFlags();
  const _filterLegacyMonitoring = (items) =>
    (items || []).filter((p) => !(_ecFlags.ec_v2_checkout_enabled && p?.product_id === 'powersure_monitoring'));
  const firstStepProducts = _filterLegacyMonitoring(recs?.first_step);
  const nextStepsCategories = (recs?.next_steps || []).map((cat) => ({
    ...cat,
    products: _filterLegacyMonitoring(cat?.products),
  }));
  const hasIntake = Boolean(intakeId);

  const firstStepRef = useRef(null);
  const nextStepsRef = useRef(null);

  const scrollToFirstStep = () => {
    firstStepRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const scrollToNextSteps = () => {
    nextStepsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const pageHeader = useMemo(() => (
    <div className="container mx-auto px-5 sm:px-6 mb-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Zap className="w-6 h-6 text-primary" />
          <span className="text-xl font-light text-primary" style={{ fontFamily: 'Outfit' }}>
            Energy Control · Startpakket
          </span>
        </div>
        <button
          onClick={() => navigate('/')}
          className="text-sm text-muted-foreground hover:text-primary"
          data-testid="ec-sk-exit"
        >
          Naar home
        </button>
      </div>
    </div>
  ), [navigate]);

  return (
    <div className="min-h-screen bg-background py-8 sm:py-10" data-testid="ec-starter-kit-page">
      {pageHeader}

      <div className="container mx-auto px-5 sm:px-6">
        <div className="max-w-3xl mx-auto space-y-8">

          {/* 1. Summary block — new commercially-softened copy + 2 CTAs */}
          <StarterKitSummary
            copy={copy}
            onPrimary={scrollToFirstStep}
            onSecondary={scrollToNextSteps}
          />

          {pilotInfo.is_pilot && (
            <div className="flex" data-testid="pilot-badge-wrap">
              <PilotBadge pilotType={pilotInfo.pilot_type} />
            </div>
          )}

          {/* Option C — EC v2 feature-grid (above checkout) */}
          <EnergyControlFeatureGrid
            email={data.email}
            intakeId={intakeId}
          />

          {/* Fase 1G — new v2 commerce CTAs, only when public flags are on */}
          <Phase1CommerceSection
            email={data.email}
            intakeId={intakeId}
          />

          {/* Option C — Meten Start bundle (low-threshold P1 + plugs + EC v2) */}
          <MetenStartBundle
            email={data.email}
            intakeId={intakeId}
            onInquiry={openLeadForm}
          />

          {/* 2. Aanbevolen eerste stap — always measure-first + trust copy */}
          <div ref={firstStepRef}>
            <StarterKitFirstStep
              loading={recsLoading}
              products={firstStepProducts}
              onInquiry={openLeadForm}
              trustCopy={copy?.trust_copy}
              intakeId={intakeId}
              customerEmail={data.email}
            />
          </div>

          {/* 3. Mogelijke vervolgstappen als de metingen dit bevestigen */}
          <div ref={nextStepsRef}>
            <StarterKitNextSteps
              loading={recsLoading}
              categories={nextStepsCategories}
              onInquiry={openLeadForm}
              intakeId={intakeId}
              customerEmail={data.email}
            />
          </div>

          {/* 4. Optioneel noodstroom-module — opt-in, residential MVP */}
          <EmergencyPowerModule
            intakeId={intakeId}
            reportId={intakeId}
            defaultEmail={data.email || ''}
          />

          {/* 4. Bottom CTA trio */}
          <div className="bg-primary/5 border border-primary/20 rounded-2xl p-5 sm:p-6" data-testid="ec-sk-bottom-ctas">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-primary mb-3">
              Kies uw vervolgstap
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <button
                onClick={scrollToFirstStep}
                className="btn-primary inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold"
                data-testid="ec-sk-bottom-view-products"
              >
                <Package className="w-4 h-4" />
                Bekijk aanbevolen producten
              </button>
              <button
                onClick={() => openLeadForm('installer_call')}
                className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold border-2 border-primary text-primary bg-white hover:bg-primary/5"
                data-testid="ec-sk-bottom-ask-install"
              >
                <Shield className="w-4 h-4" />
                Vraag veilige installatie aan
              </button>
              <button
                onClick={() => {
                  setEmailReportStatus('idle');
                  const el = document.getElementById('ec-sk-email-box');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }}
                className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold border-2 border-primary text-primary bg-white hover:bg-primary/5"
                data-testid="ec-sk-bottom-email-me"
                disabled={!hasIntake}
                title={hasIntake ? '' : 'Rond eerst de intake af'}
              >
                <Mail className="w-4 h-4" />
                Ontvang dit pakket per e-mail
              </button>
            </div>

            {/* Inline "Ontvang per e-mail" form — hidden until the button above scrolls us here */}
            {hasIntake && (
              <div id="ec-sk-email-box" className="mt-4 pt-4 border-t border-primary/20" data-testid="ec-sk-email-box">
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Stuur dit plan naar mijn e-mail
                </label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="email"
                    autoComplete="email"
                    value={pkgEmail}
                    onChange={(e) => setPkgEmail(e.target.value)}
                    placeholder="uw@email.nl"
                    disabled={emailReportStatus === 'sending' || emailReportStatus === 'success'}
                    className="flex-1 px-3 py-2 rounded-md border border-border bg-white text-sm"
                    data-testid="ec-sk-email-input"
                  />
                  <button
                    onClick={handleSendPackageEmail}
                    disabled={!pkgEmail.trim() || emailReportStatus === 'sending' || emailReportStatus === 'success'}
                    className="btn-primary inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm disabled:opacity-50"
                    data-testid="ec-sk-email-send"
                  >
                    <Mail className="w-4 h-4" />
                    {emailReportStatus === 'sending' ? 'Verzenden…' : 'Verstuur'}
                  </button>
                </div>
                {emailReportStatus === 'success' && (
                  <div className="mt-2 text-xs bg-emerald-50 border border-emerald-200 rounded px-3 py-2 text-emerald-900" data-testid="ec-sk-email-success">
                    ✓ Uw Energy Control Plan is naar u verzonden. Controleer ook uw spam of ongewenste mail.
                  </div>
                )}
                {emailReportStatus === 'error' && (
                  <div className="mt-2 text-xs bg-amber-50 border border-amber-200 rounded px-3 py-2 text-amber-900" data-testid="ec-sk-email-error">
                    {emailReportError || 'Verzenden mislukt'}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Lead form flow — appears when user clicks "Vraag hulp aan" anywhere above */}
          {leadOpen && (
            <div
              ref={leadFormRef}
              className="bg-white rounded-2xl p-5 sm:p-7 shadow-sm border-2 border-primary/20"
              data-testid="ec-sk-lead-form"
            >
              {leadStep < 3 && (
                <div className="mb-5">
                  <Progress value={(leadStep / 2) * 100} className="h-2" data-testid="ec-sk-progress" />
                  <p className="text-xs text-muted-foreground mt-1.5">
                    Stap {leadStep} van 2 · {leadStep === 1 ? 'Uw situatie' : 'Gegevens & voorkeur'}
                  </p>
                </div>
              )}

              {leadStep === 1 && (
                <StarterKitCompatibilityStep data={data} onUpdate={onUpdate} errors={errors} />
              )}
              {leadStep === 2 && (
                <StarterKitDetailsStep data={data} onUpdate={onUpdate} errors={errors} />
              )}
              {leadStep === 3 && confirmed && (
                <div className="text-center py-4" data-testid="ec-sk-confirmation">
                  <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-8 h-8 text-primary" />
                  </div>
                  <h2 className="text-2xl font-medium text-primary mb-2" style={{ fontFamily: 'Outfit' }}>
                    Aanvraag ontvangen
                  </h2>
                  <p className="text-sm text-muted-foreground leading-relaxed max-w-md mx-auto">
                    Uw adviesaanvraag is ontvangen. U krijgt ook een bevestiging per e-mail op{' '}
                    <span className="font-medium text-foreground">{data.email}</span>.
                    We sturen u de logische vervolgstap binnen 2 werkdagen.
                  </p>

                  <div className="mt-5 mx-auto max-w-md text-left bg-muted/30 border border-border rounded-xl p-4" data-testid="ec-sk-recap">
                    <div className="text-[11px] uppercase tracking-wider text-primary/70 font-medium mb-2 text-center">
                      Uw gekozen situatie
                    </div>
                    <dl className="text-sm divide-y divide-border">
                      <div className="flex justify-between gap-3 py-1.5">
                        <dt className="text-muted-foreground">Meter / P1</dt>
                        <dd className="text-right font-medium" data-testid="ec-sk-recap-meter">{RECAP_LABELS.meter_type[data.meter_type] || '—'}</dd>
                      </div>
                      <div className="flex justify-between gap-3 py-1.5">
                        <dt className="text-muted-foreground">Aansluiting</dt>
                        <dd className="text-right font-medium" data-testid="ec-sk-recap-phase">{RECAP_LABELS.phase_type[data.phase_type] || '—'}</dd>
                      </div>
                      <div className="flex justify-between gap-3 py-1.5">
                        <dt className="text-muted-foreground">Live inzicht</dt>
                        <dd className="text-right font-medium" data-testid="ec-sk-recap-live-insight">{RECAP_LABELS.live_insight[data.live_insight] || '—'}</dd>
                      </div>
                      <div className="flex justify-between gap-3 py-1.5">
                        <dt className="text-muted-foreground">Voorkeur</dt>
                        <dd className="text-right font-medium" data-testid="ec-sk-recap-preference">{RECAP_LABELS.preference[data.preference] || '—'}</dd>
                      </div>
                      {data.interested_product_id && (
                        <div className="flex justify-between gap-3 py-1.5">
                          <dt className="text-muted-foreground">Interesse in</dt>
                          <dd className="text-right font-medium" data-testid="ec-sk-recap-interest">{data.interested_product_id}</dd>
                        </div>
                      )}
                    </dl>
                  </div>

                  <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-2">
                    {intakeId && (
                      <button
                        onClick={() => navigate(`/energy-control-report/${intakeId}`)}
                        className="px-5 py-2 rounded-md border border-border hover:bg-muted text-sm"
                        data-testid="ec-sk-back-to-report"
                      >
                        Terug naar mijn rapport
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setLeadOpen(false);
                        setConfirmed(false);
                        setLeadStep(1);
                        setData(INITIAL);
                      }}
                      className="btn-primary inline-flex items-center gap-2 px-5 py-2 rounded-md text-sm"
                      data-testid="ec-sk-close-lead"
                    >
                      Terug naar producten
                    </button>
                  </div>
                </div>
              )}

              {leadStep < 3 && (
                <div className="flex justify-between mt-7 pt-5 border-t">
                  <button
                    onClick={handleLeadBack}
                    className="flex items-center gap-2 px-4 py-2 rounded-md border border-border text-foreground hover:bg-muted"
                    data-testid="ec-sk-back-btn"
                  >
                    <ArrowLeft className="w-4 h-4" /> {leadStep === 1 ? 'Sluiten' : 'Terug'}
                  </button>
                  {leadStep === 1 ? (
                    <button
                      onClick={handleLeadNext}
                      className="btn-primary inline-flex items-center gap-2 px-6 py-2 rounded-md"
                      data-testid="ec-sk-next-btn"
                    >
                      Verder <ArrowRight className="w-4 h-4" />
                    </button>
                  ) : (
                    <button
                      onClick={handleLeadSubmit}
                      disabled={loading}
                      className="btn-primary inline-flex items-center gap-2 px-6 py-2 rounded-md disabled:opacity-50"
                      data-testid="ec-sk-submit-btn"
                    >
                      {loading ? 'Verzenden…' : 'Aanvraag bevestigen'}
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* PWA install help — small, dismissible, non-blocking */}
          <PwaInstallHelp />

          {pilotInfo.is_pilot && (
            <PilotFeedbackForm intakeId={intakeId} pilotType={pilotInfo.pilot_type} />
          )}

          {/* Tiny footer note */}
          <p className="text-[11px] text-muted-foreground text-center pt-2 leading-relaxed">
            <Sparkles className="inline w-3 h-3 mr-1 align-[-1px]" />
            Powersure geeft geen harde besparingsgaranties — alle getoonde producten en prijzen zijn indicatief.
          </p>
        </div>
      </div>
    </div>
  );
}
